import { useCallback, useReducer } from 'react';

/**
 * This file contains logging related utilities and helpers.
 *
 * Can used by applications that want to include support logging but do not
 * wish to have this always occur. Instead you must opt-in to the logging by
 * either setting a cookie, `gwilogger` to any value.
 * This will work in any kind of environment that that MFEs run on.
 */
const COOKIE_NAME = 'gwilogger';
const COOKIE_REGEX = new RegExp('(^|; )' + COOKIE_NAME + '=([^;]*)');

/**
 * Custom GWI Logger initialization.
 *
 * @param {string} prefix
 *   The log prefix to use for all logging coming from this instance
 * @returns
 */
const loggerInit = (prefix) => {
  const consoleExists = typeof console != 'undefined';
  const loggingCookieExists = (() => COOKIE_REGEX.test(document.cookie))();

  // no logging happens by default
  // check if a cookie exists that means we should opt-in to logging
  if (consoleExists && loggingCookieExists) {
    const debugMessage = `${prefix} :: %s`;
    return {
      log: window.console.log.bind(window.console, debugMessage),
      error: window.console.error.bind(window.console, debugMessage),
      info: window.console.info.bind(window.console, debugMessage),
      warn: window.console.warn.bind(window.console, debugMessage),
      group: window.console.group.bind(window.console, debugMessage),
      groupEnd: window.console.groupEnd.bind(window.console)
    };
  } else {
    const noOp = () => {};
    return {
      log: noOp,
      error: noOp,
      warn: noOp,
      info: noOp,
      group: noOp,
      groupEnd: noOp
    };
  }
};

// Based on: https://github.com/Zaelot-Inc/use-reducer-logger/blob/master/srhfl.js#L12
// A very very basic logger for the useReducer function in the React Hooks API.
// Inspired by redux-logger.
const reducerLogger = (reducer, logger) => {
  const reducerWithLogger = useCallback(
    (state, action) => {
      const next = reducer(state, action);
      logger.group(`Action: %c${action.type}`, 'font-weight: bold;');
      logger.log('%cPrevious State:', 'color: #9E9E9E; font-weight: 700;', state);
      logger.log('%cAction:', 'color: #00A7F7; font-weight: 700;', action);
      logger.log('%cNext State:', 'color: #47B04B; font-weight: 700;', next);
      logger.groupEnd();
      return next;
    },
    [reducer]
  );

  return reducerWithLogger;
};

/**
 * If the logger argument is passed, you can use the same format
 * like React.useReducer()

 * @param logger
 * @returns hook
 */
const useReducerWithLogger = (logger) => (reducer, defaultState) => {
  return useReducer(reducerLogger(reducer, logger), defaultState);
};

export { loggerInit, useReducerWithLogger, reducerLogger };
